import { FC, useEffect, useState } from 'react';

import { BodySmall, Box, Modal, ModalFooter, ModalHeader, Skeleton } from '@parspec/pixel';
import { useGenerateRecoveryCodeMutation } from 'src/features/Authentication/queries';
import { copyMsg } from 'src/features/shared/constants';

interface RecoveryCodeModalProps {
    open: boolean;
    onClose: () => void;
    handleSnackbarMsg: (msg: string) => void;
}

const RecoveryCodeModal: FC<RecoveryCodeModalProps> = ({ open, onClose, handleSnackbarMsg }) => {
    const [codes, setCodes] = useState<string[]>([]);
    const { mutateAsync: generateRecoveryCodes, isLoading } = useGenerateRecoveryCodeMutation();

    useEffect(() => {
        const getCodes = async () => {
            const response = await generateRecoveryCodes();
            setCodes(response?.data);
        };
        getCodes();
    }, []);

    const handleDownloadCodes = () => {
        if (!codes.length) return;
        const content = codes.join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'parspec-recovery-codes.txt';
        link.click();
    };

    const handleCopyCodes = async () => {
        if (!codes.length) return;
        const content = codes.join('\n');
        await navigator.clipboard.writeText(content);
        handleSnackbarMsg(copyMsg);
    };

    return (
        <Modal
            header={<ModalHeader title="Recovery Codes" onClose={onClose} />}
            footer={
                <ModalFooter
                    onAccept={handleDownloadCodes}
                    helperText={
                        <Box display="flex" justifyContent="flex-end" width={1}>
                            <Box onClick={() => onClose()} sx={{ cursor: 'pointer' }}>
                                <BodySmall color="primary.main">Cancel</BodySmall>
                            </Box>
                        </Box>
                    }
                    onReject={handleCopyCodes}
                    continueButtonLabel="Download"
                    cancelButtonLabel="Copy"
                    disabled={isLoading}
                />
            }
            open={open}
        >
            <Box width={350}>
                <Box bgcolor="primary.light" border="1px solid" borderColor="primary.main" py={1} px={2} borderRadius={1}>
                    <Box display={'flex'} alignItems={'flex-end'}>
                        <BodySmall limit={false} color="primary.main">
                            Recovery codes are one-time passcodes for emergency account access if your second factor is lost.
                            <span style={{ fontWeight: 700 }}> Download or print them, and store securely like your password.</span>
                        </BodySmall>
                    </Box>
                </Box>
                {isLoading ? (
                    <Box mt={4}>
                        <Skeleton height="300px" variant="rectangular" />
                    </Box>
                ) : (
                    <Box mt={4} bgcolor="neutral.light" py={2} px={3} display="flex" flexDirection="column" gap={2} overflow="auto" maxHeight={300}>
                        {codes.map((code) => (
                            <Box key={code}>
                                <BodySmall color="text.secondary">{code}</BodySmall>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default RecoveryCodeModal;
